import {
  DRIVE_ACCOUNT_EMAIL_STORAGE_KEY,
  DRIVE_FOLDER_CACHE_KEY,
  DRIVE_LINKED_STORAGE_KEY,
  DRIVE_OAUTH_SCOPES,
  GIS_SCRIPT_URL,
  getGoogleClientId,
  isGoogleDriveEnabled,
} from "./constants";
import {
  clearDriveAuthSession,
  hydrateDriveAuthSessionFromIdb,
  persistDriveAuthSession,
  readSessionFromLocalStorage,
} from "./authSessionStore";
import { DriveAuthError, DriveNotConfiguredError } from "./errors";
import { notifyDriveLinked } from "./driveAutoSyncNotify";
import {
  isOAuthProxyEnabled,
  refreshViaOAuthProxy,
  revokeOAuthProxySession,
  signInViaOAuthProxy,
} from "./oauthProxy";

import type { DriveAuthSession } from "./types";

let gisLoadPromise: Promise<void> | null = null;
let driveAuthInitPromise: Promise<boolean> | null = null;
const pendingTokenRequests = new Map<string, Promise<DriveAuthSession>>();

const isGisReady = (): boolean => !!window.google?.accounts?.oauth2;

const waitForGisReady = (): Promise<void> =>
  new Promise((resolve, reject) => {
    let attempts = 0;
    const poll = () => {
      if (isGisReady()) {
        resolve();
        return;
      }
      if (++attempts < 50) {
        setTimeout(poll, 20);
      } else {
        reject(new DriveAuthError("Could not load Google sign-in."));
      }
    };
    poll();
  });

const loadGisScriptInner = async (): Promise<void> => {
  const existing = document.querySelector(
    `script[src="${GIS_SCRIPT_URL}"]`,
  ) as HTMLScriptElement | null;

  if (existing) {
    if (isGisReady()) {
      return;
    }
    await Promise.race([
      waitForGisReady(),
      new Promise<void>((_resolve, reject) => {
        existing.addEventListener(
          "error",
          () => reject(new DriveAuthError("Could not load Google sign-in.")),
          { once: true },
        );
      }),
    ]);
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GIS_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new DriveAuthError("Could not load Google sign-in."));
    document.head.appendChild(script);
  });
  await waitForGisReady();
};

const loadGisScript = (): Promise<void> => {
  if (typeof window === "undefined") {
    return Promise.reject(new DriveAuthError("Google sign-in requires a browser."));
  }
  if (isGisReady()) {
    return Promise.resolve();
  }
  if (!gisLoadPromise) {
    gisLoadPromise = loadGisScriptInner().catch((error) => {
      gisLoadPromise = null;
      throw error;
    });
  }
  return gisLoadPromise;
};

/** Preload GIS when using token model (not needed for OAuth proxy). */
export const preloadGoogleDriveAuth = (): Promise<void> => {
  if (!isGoogleDriveEnabled() || isOAuthProxyEnabled()) {
    return Promise.resolve();
  }
  return loadGisScript().catch(() => {});
};

/** Hydrate stored token once per app load. */
export const initDriveAuth = (): Promise<boolean> => {
  if (!isGoogleDriveEnabled()) {
    return Promise.resolve(false);
  }
  if (!driveAuthInitPromise) {
    driveAuthInitPromise = (async () => {
      void preloadGoogleDriveAuth();
      return hydrateDriveAuthSessionFromIdb();
    })();
  }
  return driveAuthInitPromise;
};

export const hydrateDriveAuthSession = (): Promise<boolean> => initDriveAuth();

const readStoredSession = (): DriveAuthSession | null => {
  const session = readSessionFromLocalStorage();
  if (!session) {
    return null;
  }
  return { accessToken: session.accessToken, expiresAt: session.expiresAt };
};

const readStoredAccountEmail = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }
  return localStorage.getItem(DRIVE_ACCOUNT_EMAIL_STORAGE_KEY)?.trim() || undefined;
};

const persistAccountEmail = (email: string | undefined): void => {
  if (typeof window === "undefined") {
    return;
  }
  if (email) {
    localStorage.setItem(DRIVE_ACCOUNT_EMAIL_STORAGE_KEY, email);
  } else {
    localStorage.removeItem(DRIVE_ACCOUNT_EMAIL_STORAGE_KEY);
  }
};

export const isGoogleDriveLinked = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  if (localStorage.getItem(DRIVE_LINKED_STORAGE_KEY) === "true") {
    return true;
  }
  if (readStoredSession()) {
    markGoogleDriveLinked();
    return true;
  }
  return false;
};

const markGoogleDriveLinked = (): void => {
  localStorage.setItem(DRIVE_LINKED_STORAGE_KEY, "true");
};

const clearGoogleDriveLinked = (): void => {
  localStorage.removeItem(DRIVE_LINKED_STORAGE_KEY);
  localStorage.removeItem(DRIVE_ACCOUNT_EMAIL_STORAGE_KEY);
};

const fetchUserEmail = async (accessToken: string): Promise<string | undefined> => {
  try {
    const response = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
      return undefined;
    }
    const data = (await response.json()) as { email?: string };
    return data.email;
  } catch {
    return undefined;
  }
};

export const getAccessToken = (): string | null =>
  readStoredSession()?.accessToken ?? null;

export const hasValidAccessToken = (): boolean => !!getAccessToken();

export const isSignedInToGoogle = (): boolean => isGoogleDriveLinked();

export const getGoogleAccountEmail = async (): Promise<string | undefined> => {
  const cached = readStoredAccountEmail();
  const token = getAccessToken();
  if (!token) {
    return cached;
  }
  const email = await fetchUserEmail(token);
  if (email) {
    persistAccountEmail(email);
  }
  return email ?? cached;
};

type RequestTokenOptions = {
  prompt: "none" | "" | "consent";
  loginHint?: string;
};

type SignInOptions = {
  forceConsent?: boolean;
};

const tokenRequestKey = (options: RequestTokenOptions): string =>
  `${options.prompt}\0${options.loginHint ?? ""}`;

const isInteractionRequiredError = (error: unknown): boolean => {
  if (!(error instanceof DriveAuthError)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return (
    message.includes("interaction_required") ||
    message.includes("login_required") ||
    message.includes("consent_required") ||
    message.includes("user logged out")
  );
};

const isConsentRequiredError = (error: unknown): boolean => {
  if (!(error instanceof DriveAuthError)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return message.includes("consent_required");
};

const requestGoogleAccessToken = (
  options: RequestTokenOptions,
): Promise<DriveAuthSession> => {
  const key = tokenRequestKey(options);
  const existing = pendingTokenRequests.get(key);
  if (existing) {
    return existing;
  }

  const promise = (async () => {
    if (!isGoogleDriveEnabled()) {
      throw new DriveNotConfiguredError();
    }
    const clientId = getGoogleClientId();
    if (!clientId) {
      throw new DriveNotConfiguredError();
    }

    if (!isGisReady()) {
      await loadGisScript();
    }

    return new Promise<DriveAuthSession>((resolve, reject) => {
      try {
        const tokenClient = window.google!.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: DRIVE_OAUTH_SCOPES,
          callback: async (response) => {
            if (response.error || !response.access_token) {
              reject(
                new DriveAuthError(
                  response.error_description || response.error || undefined,
                ),
              );
              return;
            }
            const expiresIn = response.expires_in ?? 3600;
            const session = await persistDriveAuthSession(
              response.access_token,
              expiresIn,
            );
            markGoogleDriveLinked();
            const email = await fetchUserEmail(response.access_token);
            persistAccountEmail(email);
            notifyDriveLinked();
            resolve({
              accessToken: session.accessToken,
              expiresAt: session.expiresAt,
              email,
            });
          },
        });

        const requestOptions: {
          prompt?: string;
          login_hint?: string;
        } = {};
        if (options.prompt !== undefined) {
          requestOptions.prompt = options.prompt;
        }
        if (options.loginHint) {
          requestOptions.login_hint = options.loginHint;
        }
        tokenClient.requestAccessToken(requestOptions);
      } catch (error) {
        reject(
          error instanceof Error
            ? error
            : new DriveAuthError("Google sign-in could not start."),
        );
      }
    });
  })().finally(() => {
    pendingTokenRequests.delete(key);
  });

  pendingTokenRequests.set(key, promise);
  return promise;
};

const refreshLinkedAccessTokenSilently = async (
  loginHint?: string,
): Promise<string | null> => {
  try {
    const session = await requestGoogleAccessToken({
      prompt: "none",
      loginHint,
    });
    return session.accessToken;
  } catch (error) {
    if (!isInteractionRequiredError(error)) {
      if (error instanceof DriveAuthError) {
        await clearDriveAuthSession();
      }
      return null;
    }
  }

  try {
    const session = await requestGoogleAccessToken({
      prompt: "",
      loginHint,
    });
    return session.accessToken;
  } catch {
    return null;
  }
};

const refreshLinkedAccessToken = async (loginHint?: string): Promise<string> => {
  const silent = await refreshLinkedAccessTokenSilently(loginHint);
  if (silent) {
    return silent;
  }

  const session = await requestGoogleAccessToken({
    prompt: "consent",
    loginHint,
  });
  return session.accessToken;
};

const refreshAccessTokenSilently = async (): Promise<string | null> => {
  if (isOAuthProxyEnabled()) {
    const session = await refreshViaOAuthProxy();
    return session?.accessToken ?? null;
  }
  return refreshLinkedAccessTokenSilently(readStoredAccountEmail());
};

const completeProxySignIn = async (
  session: DriveAuthSession,
): Promise<DriveAuthSession> => {
  markGoogleDriveLinked();
  if (session.email) {
    persistAccountEmail(session.email);
  }
  notifyDriveLinked();
  return session;
};

export const warmDriveAccessToken = async (): Promise<boolean> => {
  if (getAccessToken()) {
    return true;
  }

  await initDriveAuth();

  if (getAccessToken()) {
    return true;
  }

  if (!isGoogleDriveLinked()) {
    return false;
  }

  const token = await refreshAccessTokenSilently();
  return !!token;
};

export const signInWithGoogle = async (
  options?: SignInOptions,
): Promise<DriveAuthSession> => {
  const loginHint = readStoredAccountEmail();

  if (isOAuthProxyEnabled()) {
    const session = await signInViaOAuthProxy({
      forceConsent: options?.forceConsent || !isGoogleDriveLinked(),
      loginHint,
    });
    return completeProxySignIn(session);
  }

  if (options?.forceConsent) {
    return requestGoogleAccessToken({ prompt: "consent", loginHint });
  }
  const prompt = isGoogleDriveLinked() ? "" : "consent";
  try {
    return await requestGoogleAccessToken({ prompt, loginHint });
  } catch (error) {
    if (isGoogleDriveLinked() && isConsentRequiredError(error)) {
      return requestGoogleAccessToken({ prompt: "consent", loginHint });
    }
    throw error;
  }
};

export const ensureAccessToken = async (): Promise<string> => {
  const existing = getAccessToken();
  if (existing) {
    return existing;
  }

  await initDriveAuth();

  const hydrated = getAccessToken();
  if (hydrated) {
    return hydrated;
  }

  if (!isGoogleDriveLinked()) {
    throw new DriveAuthError("Sign in with Google first.");
  }

  if (isOAuthProxyEnabled()) {
    const session = await refreshViaOAuthProxy();
    if (session?.accessToken) {
      if (session.email) {
        persistAccountEmail(session.email);
      }
      return session.accessToken;
    }
    const signedIn = await signInViaOAuthProxy({
      loginHint: readStoredAccountEmail(),
    });
    await completeProxySignIn(signedIn);
    return signedIn.accessToken;
  }

  return refreshLinkedAccessToken(readStoredAccountEmail());
};

/** For auto-sync: refresh silently when OAuth proxy is enabled. */
export const tryRefreshAccessToken = async (): Promise<boolean> => {
  if (getAccessToken()) {
    return true;
  }
  if (!isGoogleDriveLinked()) {
    return false;
  }
  return !!(await refreshAccessTokenSilently());
};

export const signOutFromGoogle = async (): Promise<void> => {
  const token = getAccessToken();
  if (token && window.google?.accounts?.oauth2) {
    window.google.accounts.oauth2.revoke(token, () => {});
  }
  if (isOAuthProxyEnabled()) {
    await revokeOAuthProxySession();
  }
  await clearDriveAuthSession();
  clearGoogleDriveLinked();
  localStorage.removeItem(DRIVE_FOLDER_CACHE_KEY);
  sessionStorage.removeItem(DRIVE_FOLDER_CACHE_KEY);
  driveAuthInitPromise = null;
};

export const handleDriveAuthFailure = (): void => {
  void clearDriveAuthSession();
  localStorage.removeItem(DRIVE_FOLDER_CACHE_KEY);
  sessionStorage.removeItem(DRIVE_FOLDER_CACHE_KEY);
};
